/* eslint-disable complexity */
import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom-v5-compat'
import { Form, Input, Select } from 'components/forms'
import { ageNextBirthday } from 'lib/date'
import { InfoButton } from 'components/buttons'
import PhotoUploader from '../photo-uploader'
import Slide from '../slide/slide'
import css from './KidNameAndBirthdate.styles.scss'

const BIRTHDATE_FUTURE_ERROR = 'Birthdate must not be in the future.'
const BIRTHDATE_INVALID_ERROR = 'Please enter a valid birthdate.'
export const DEFAULT_TITLE = 'Who is this wishlist for?'

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const currentYear = new Date().getFullYear()
const oneYearAgo = new Date()
oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

const isValidDate = (date: Date) =>
  date instanceof Date && !Number.isNaN(date.getTime())

interface KidNameAndBirthdateProps {
  initialBirthdate?: string
  initialFirstName?: string
  initialPhotoUrl?: string
  nextPath: string
  onSubmit: (data: any) => void
  title?: string
}

export const KidNameAndBirthdate = ({
  initialBirthdate = '',
  initialFirstName = '',
  initialPhotoUrl = '',
  nextPath,
  onSubmit,
  title,
}: KidNameAndBirthdateProps) => {
  const initialDate = initialBirthdate ? new Date(initialBirthdate) : new Date()
  const [birthDay, setBirthDay] = useState<number | null>(initialDate.getDate())
  const [birthYear, setBirthYear] = useState<number>(
    initialBirthdate ? initialDate.getFullYear() : currentYear - 1
  )
  const [birthMonth, setBirthMonth] = useState<number | null>(
    initialDate.getMonth() + 1
  )

  const isCompleteDate =
    birthYear !== null && birthMonth !== null && birthDay !== null
  const combinedBirthdate = isCompleteDate
    ? new Date(birthYear, birthMonth - 1, birthDay)
    : null

  const [birthdateError, setBirthdateError] = useState<string>('')
  const [photoUrl, setPhotoUrl] = useState<string>(initialPhotoUrl)

  const { register, handleSubmit, errors, reset } = useForm()

  useEffect(() => {
    reset({
      first_name: initialFirstName,
    })
  }, [reset, initialFirstName])

  useEffect(() => {
    if (combinedBirthdate !== null) {
      const now = new Date()
      if (combinedBirthdate >= now) {
        setBirthdateError(BIRTHDATE_FUTURE_ERROR)
      } else if (!isValidDate(combinedBirthdate)) {
        setBirthdateError(BIRTHDATE_INVALID_ERROR)
      } else {
        setBirthdateError('')
      }
    }
  }, [birthMonth, birthDay, birthYear])

  const navigate = useNavigate()

  const handleBirthMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setBirthMonth(Number(e.target.value))
    },
    []
  )

  const handleBirthDayChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setBirthDay(Number(e.target.value))
    },
    []
  )

  const handleBirthYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setBirthYear(Number(e.target.value))
    },
    []
  )

  const handleOnSubmit = (data: Record<string, any>) => {
    if (combinedBirthdate !== null) {
      onSubmit({
        first_name: data.first_name.trim(),
        birthdate: combinedBirthdate.toISOString().split('T')[0],
        registry: { photo_url: photoUrl },
      })
      navigate(nextPath)
    }
  }

  const handlePhotoUpload = (url: string) => setPhotoUrl(url)

  return (
    <Slide title={title || DEFAULT_TITLE}>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <PhotoUploader value={photoUrl} onChange={handlePhotoUpload} />
        <Input
          error={errors?.first_name?.message}
          inputRef={register({
            required: 'First name is required',
          })}
          label="First Name"
          name="first_name"
          type="text"
        />
        <div className={css.KidNameAndBirthdate__datePickerWrapper}>
          <div className={css.KidNameAndBirthdate__monthDayRow}>
            <div>
              <Select
                id="birthMonth"
                label="Birth Month"
                name="birthMonth"
                value={birthMonth}
                onChange={handleBirthMonthChange}
              >
                {months.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                id="birthDay"
                label="Birth Day"
                name="birthDay"
                value={birthDay}
                onChange={handleBirthDayChange}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <Select
              id="birthYear"
              label="What year were they born?"
              name="birthYear"
              value={birthYear}
              onChange={handleBirthYearChange}
            >
              {Array.from(
                { length: currentYear - 1899 },
                (_, i) => currentYear - i
              ).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </div>
          {isCompleteDate &&
            combinedBirthdate &&
            isValidDate(combinedBirthdate) && (
              <div className={css.KidNameAndBirthdate__turningAge}>
                Turning {ageNextBirthday(combinedBirthdate)}!
              </div>
            )}
        </div>
        {birthdateError && (
          <p className="has-error danger small">{birthdateError}</p>
        )}
        <p className={css.KidNameAndBirthdate__subcopy}>
          We'll use the birth year to show you gift recommendations.
        </p>
        <InfoButton
          block
          pill
          aria-label="submit"
          className="mbl"
          disabled={!!Object.keys(errors).length || birthdateError}
          type="submit"
        >
          Next
        </InfoButton>
      </Form>
    </Slide>
  )
}
